package com.islamicmedia.shared.domain.repository

import com.islamicmedia.shared.data.api.AnthropicApiClient
import com.islamicmedia.shared.data.api.SseEvent
import com.islamicmedia.shared.data.cache.CacheService
import com.islamicmedia.shared.data.auth.SupabaseAuthService
import com.islamicmedia.shared.domain.model.AnalysisProgress
import com.islamicmedia.shared.domain.model.AnalysisResult
import com.islamicmedia.shared.domain.model.DailyLimitNotice
import com.islamicmedia.shared.domain.model.KIDS_NOT_ALLOWED_CODE
import com.islamicmedia.shared.domain.model.KidsBlock
import com.islamicmedia.shared.domain.model.KidsProgress
import com.islamicmedia.shared.domain.model.KidsCacheRead
import com.islamicmedia.shared.domain.model.KidsResult
import com.islamicmedia.shared.domain.model.LimitRequestResult
import com.islamicmedia.shared.domain.model.MediaType
import com.islamicmedia.shared.domain.model.SearchCriteria
import com.islamicmedia.shared.domain.model.TldrPoint
import com.islamicmedia.shared.util.currentTimeMillis
import com.islamicmedia.shared.util.parseTimestampMillis
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.FlowPreview
import kotlinx.coroutines.flow.timeout
import kotlin.time.Duration.Companion.milliseconds
import kotlinx.coroutines.delay
import kotlinx.coroutines.withTimeout
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.channelFlow
import kotlinx.coroutines.flow.flow
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.double
import kotlinx.serialization.json.doubleOrNull
import kotlinx.serialization.json.int
import kotlinx.serialization.json.intOrNull
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.long
import kotlinx.serialization.json.longOrNull

/** No frame at all for this long on a kid's stream = the connection is dead (heartbeats are every 15s). */
private const val KIDS_STALL_MS = 50_000L

/** How long a kids request waits for the session to be readable before it goes out anyway (see [MediaCheckerRepository.awaitKidsToken]). */
private const val KIDS_TOKEN_WAIT_MS = 15_000L

/**
 * V2 thin repository — all pipeline logic runs on the server.
 * This class just makes 2 HTTP calls and parses SSE events into AnalysisProgress.
 */
class MediaCheckerRepository(
    private val apiClient: AnthropicApiClient,
    private val cacheService: CacheService? = null  // kept for admin dashboard
) {
    private val json = Json { ignoreUnknownKeys = true }

    /**
     * "Request additional searches" from the weekly-limit dialog: sends [reason] to the server, which
     * saves it and emails support. See [AnthropicApiClient.requestMoreSearches]. Never throws.
     */
    suspend fun requestMoreSearches(reason: String): LimitRequestResult = apiClient.requestMoreSearches(reason)

    /** Failure tags from [AnthropicApiClient.cacheFailureReason] that mean "we had no usable
     *  credential", as opposed to "the result genuinely isn't there". Only these are worth waiting
     *  on a session for — `not_found` / `not_purchased` would wait 60s to fail identically. */
    private val AUTH_RECOVERABLE_REASONS = setOf("auth_no_session", "auth_rejected")

    /** How long to wait for the session to become readable before giving up on a paid result.
     *  Generous on purpose: `RefreshFailure` retries on a 10s cadence, so this covers several
     *  cycles, and the alternative is discarding an analysis the user has already been charged for. */
    private val AUTH_RECOVERY_TIMEOUT_MS = 60_000L

    /**
     * V2: Calls /api/disambiguate on the server.
     */
    fun disambiguate(userInput: String): Flow<AnalysisProgress> = flow {
        emit(AnalysisProgress.Disambiguating(userInput))
        try {
            val result = apiClient.postDisambiguate(userInput)
            emit(AnalysisProgress.DisambiguationComplete(result.items, result.journeyId))
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            emit(AnalysisProgress.Error(friendlyErrorMessage(e)))
        }
    }

    /**
     * V2: Connects to /api/analyze SSE stream on the server.
     * Parses each SSE event into the corresponding AnalysisProgress.
     */
    fun analyze(
        title: String,
        mediaType: MediaType,
        criteria: SearchCriteria,
        year: String? = null,
        info: String? = null,
        author: String? = null,
        journeyId: String? = null,
        quickCheck: Boolean = false,
        upgradeFromQuick: Boolean = false,
        /** How the user reached this analysis (autocomplete/disambiguation/photo/isbn/recommendation
         *  rows/upgrade). Stamped onto the analysis_complete analytics event server-side. */
        searchSource: String? = null,
        /** Handle /api/cancel uses to kill this pipeline. Unlike [journeyId] it exists for every
         *  entry point, so cancelling works even when no disambiguation step ran. */
        cancelToken: String? = null
    ): Flow<AnalysisProgress> = channelFlow {
        try {
            // TODO: Re-enable after v2.0.1+ is live in stores
            // Ensure token is fresh before the long-running analysis
            // apiClient.refreshTokenIfNeeded()

            // Build the request body
            val requestBody = buildAnalyzeRequestBody(title, mediaType, criteria, year, info, author, journeyId, quickCheck, upgradeFromQuick, searchSource, cancelToken)

            // Connect to SSE stream and parse events
            // Cancel collection after the final event to cleanly close the SSE connection
            // (prevents iOS -1005 "network connection lost" errors from dangling connections)
            // Overall 10-minute timeout prevents the app getting stuck forever if
            // the SSE connection silently dies (common on iOS).
            // Build the request body without journeyId for cache lookups
            val cacheRequestBody = buildAnalyzeRequestBody(title, mediaType, criteria, year, info, author, null, quickCheck)

            withTimeout(600_000L) {
                apiClient.startAnalysisSse(requestBody).collect { event ->
                    val progress = parseSseEvent(event, cacheRequestBody)
                    if (progress != null) {
                        send(progress)
                        if (progress is AnalysisProgress.AnalysisComplete || progress is AnalysisProgress.Error ||
                            progress is AnalysisProgress.DailyLimitReached) {
                            this@channelFlow.close()
                            return@collect
                        }
                    }
                }
            }
        } catch (e: kotlinx.coroutines.TimeoutCancellationException) {
            println("[V2-ANALYZE] SSE timed out after 10 minutes")
            send(AnalysisProgress.Error("The analysis took too long. Please try again."))
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            println("[V2-ANALYZE] SSE error: ${e::class.simpleName}: ${e.message}")
            send(AnalysisProgress.Error(friendlyErrorMessage(e)))
        }
    }

    /**
     * Reconnect to an in-flight server pipeline by journeyId. Used after an SSE drop (typically
     * the OS suspending the app) to recover the existing analysis without starting a duplicate.
     *
     * The original analysis parameters are still required because the SSE `complete` event is
     * lightweight — we use those parameters to build the cache lookup body for fetching the full
     * result. If the server reports `not_in_flight`, the caller should fall back to retry.
     */
    fun resumeAnalysis(
        title: String,
        mediaType: MediaType,
        criteria: SearchCriteria,
        year: String? = null,
        info: String? = null,
        author: String? = null,
        journeyId: String,
        quickCheck: Boolean = false
    ): Flow<AnalysisProgress> = channelFlow {
        try {
            val cacheRequestBody = buildAnalyzeRequestBody(title, mediaType, criteria, year, info, author, null, quickCheck)
            withTimeout(600_000L) {
                apiClient.resumeAnalysisSse(journeyId).collect { event ->
                    val progress = parseSseEvent(event, cacheRequestBody)
                    if (progress != null) {
                        send(progress)
                        if (progress is AnalysisProgress.AnalysisComplete || progress is AnalysisProgress.Error ||
                            progress is AnalysisProgress.DailyLimitReached) {
                            this@channelFlow.close()
                            return@collect
                        }
                    }
                }
            }
        } catch (e: kotlinx.coroutines.TimeoutCancellationException) {
            send(AnalysisProgress.Error("The analysis took too long. Please try again."))
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            println("[V2-RESUME] SSE error: ${e::class.simpleName}: ${e.message}")
            send(AnalysisProgress.Error(friendlyErrorMessage(e)))
        }
    }

    /**
     * Re-fetch the current canonical result for an already-analyzed title. Used by the app-open
     * version sync to pull in admin edits (concerns/score/cover) for EXISTING users. Goes through
     * /api/cached-result (re-synthesized fresh from media_phase1/phase2/media_content; the user's
     * own titles read free). Returns null on a cache miss or transport error.
     */
    suspend fun refetchResult(
        title: String,
        mediaType: MediaType,
        criteria: SearchCriteria,
        year: String? = null,
        quickCheck: Boolean = false
    ): AnalysisResult? {
        return try {
            val body = buildAnalyzeRequestBody(title, mediaType, criteria, year, null, null, null, quickCheck)
            val resultJson = apiClient.fetchCachedResult(body) ?: return null
            json.decodeFromString<AnalysisResult>(resultJson)
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            println("[VERSION-SYNC] refetch failed for $title: ${e.message}")
            null
        }
    }

    /**
     * The write-ups finished so far for a FULL analysis that is still running. Units not done yet
     * come back with enrichmentStatus "pending". Used to fill each concern card as soon as its own
     * write-up lands instead of all at once at the end. Null on a miss or transport error.
     */
    suspend fun fetchPartialResult(
        title: String,
        mediaType: MediaType,
        criteria: SearchCriteria,
        year: String?,
        info: String?,
        author: String?,
    ): AnalysisResult? {
        val body = buildAnalyzeRequestBody(title, mediaType, criteria, year, info, author, null, false)
        val resultJson = apiClient.fetchCachedResult(body, partial = true) ?: return null
        return try {
            json.decodeFromString<AnalysisResult>(resultJson)
        } catch (e: Exception) {
            println("[PARTIAL] Unparseable partial result: ${e.message}")
            null
        }
    }

    /**
     * Probe the cache for an already-finished result. Used by the client's stream-liveness watchdog:
     * when the SSE connection dies silently the pipeline keeps running server-side and caches its
     * result, so this recovers it without waiting out the 600s socket timeout.
     *
     * Builds the request through the SAME [buildAnalyzeRequestBody] the SSE path uses — the criteria
     * hash is part of the cache key, so a hand-rolled body here would miss the row it's looking for.
     * Null means "not finished yet" or a transport error; both mean "keep waiting".
     */
    suspend fun fetchCompletedResult(
        title: String,
        mediaType: MediaType,
        criteria: SearchCriteria,
        year: String?,
        info: String?,
        author: String?,
        quickCheck: Boolean
    ): AnalysisResult? {
        val body = buildAnalyzeRequestBody(title, mediaType, criteria, year, info, author, null, quickCheck)
        val resultJson = apiClient.fetchCachedResult(body) ?: return null
        return try {
            json.decodeFromString<AnalysisResult>(resultJson)
        } catch (e: Exception) {
            println("[CACHE-PROBE] Unparseable cached result: ${e.message}")
            null
        }
    }

    private fun buildAnalyzeRequestBody(
        title: String,
        mediaType: MediaType,
        criteria: SearchCriteria,
        year: String?,
        info: String?,
        author: String? = null,
        journeyId: String? = null,
        quickCheck: Boolean = false,
        upgradeFromQuick: Boolean = false,
        searchSource: String? = null,
        cancelToken: String? = null,
        kids: Boolean = false
    ): String {
        val escapedTitle = title.replace("\"", "\\\"")
        val escapedInfo = info?.replace("\"", "\\\"")
        val escapedAuthor = author?.replace("\"", "\\\"")
        val escapedCustom = criteria.customConcerns.replace("\"", "\\\"")
        val ageRangesJson = criteria.childAgeRanges.joinToString(",") { "\"$it\"" }

        return buildString {
            append("{")
            append("\"title\":\"$escapedTitle\",")
            append("\"mediaType\":\"${mediaType.name}\",")
            if (year != null) append("\"year\":\"$year\",")
            if (escapedInfo != null) append("\"info\":\"$escapedInfo\",")
            if (escapedAuthor != null) append("\"author\":\"$escapedAuthor\",")
            if (journeyId != null) append("\"journey_id\":\"$journeyId\",")
            // Deliberately excluded from the cache-lookup body (built with cancelToken = null), the
            // same way journey_id is — it identifies the RUN, not the thing being analyzed.
            if (cancelToken != null) append("\"cancel_token\":\"$cancelToken\",")
            if (quickCheck) append("\"quick_check\":true,")
            if (upgradeFromQuick) append("\"upgrade_from_quick\":true,")
            if (searchSource != null) append("\"search_source\":\"$searchSource\",")
            if (kids) append("\"kids\":true,")
            append("\"criteria\":{")
            append("\"childAgeRanges\":[$ageRangesJson],")
            append("\"customConcerns\":\"$escapedCustom\"")
            append("}}")
        }
    }

    // ── Kids Mode ───────────────────────────────────────────────────────────────
    // A separate, deliberately small path: the server sends a kid-filtered stream (progress + one
    // `kids_result`), and nothing here ever decodes an AnalysisResult. Anything unexpected is
    // ignored or treated as a failure — Kids Mode fails CLOSED.

    /** Kids Mode analysis over /api/analyze with kids=true. Ends at the first Done/Blocked/Failed. */
    fun analyzeKids(
        title: String,
        mediaType: MediaType,
        criteria: SearchCriteria,
        year: String? = null,
        info: String? = null,
        author: String? = null,
        journeyId: String? = null,
        searchSource: String? = null,
        cancelToken: String? = null
    ): Flow<KidsProgress> = kidsFlow {
        awaitKidsToken()
        val body = buildAnalyzeRequestBody(title, mediaType, criteria, year, info, author, journeyId,
            searchSource = searchSource, cancelToken = cancelToken, kids = true)
        apiClient.startAnalysisSse(body)
    }

    /** Reattach to a kid's in-flight analysis after an SSE drop (app backgrounded). */
    fun resumeKids(journeyId: String): Flow<KidsProgress> = kidsFlow {
        awaitKidsToken()
        apiClient.resumeAnalysisSse(journeyId, kids = true)
    }

    /**
     * Kids requests wait for a readable session instead of going out with a missing token. On a cold
     * start (or after a background excursion) the token is momentarily unreadable although the
     * session is fine, and the server answers a token-less kids request "sign in", which would turn a
     * valid check into "Ask a grown-up". Gives up after [KIDS_TOKEN_WAIT_MS] and sends anyway, so the
     * server has the last word.
     */
    private suspend fun awaitKidsToken() {
        SupabaseAuthService.instance.awaitAccessToken(KIDS_TOKEN_WAIT_MS)
    }

    /** The kid result for an already-analyzed title (/api/cached-result, kids=true). */
    suspend fun fetchKidsResult(
        title: String,
        mediaType: MediaType,
        criteria: SearchCriteria,
        year: String?,
        info: String?,
        author: String?
    ): KidsCacheRead {
        awaitKidsToken()
        val body = buildAnalyzeRequestBody(title, mediaType, criteria, year, info, author, null, kids = true)
        val resultJson = when (val outcome = apiClient.fetchCachedResultDetailed(body)) {
            is AnthropicApiClient.CacheFetchOutcome.Success -> outcome.json
            // The server refused this account: not an error to retry, "Ask a grown-up".
            is AnthropicApiClient.CacheFetchOutcome.Failed ->
                return if (outcome.reason == KIDS_NOT_ALLOWED_CODE) KidsCacheRead.NotAllowed else KidsCacheRead.NotReady
        }
        return try {
            KidsCacheRead.Ready(json.decodeFromString<KidsResult>(resultJson))
        } catch (_: Exception) {
            // An adult result (it has concerns) = a server that ignored kids=true. Never shown.
            if (resultJson.contains("\"concerns\"")) KidsCacheRead.Unsupported else KidsCacheRead.NotReady
        }
    }

    @OptIn(FlowPreview::class)
    private fun kidsFlow(open: suspend () -> Flow<SseEvent>): Flow<KidsProgress> = channelFlow {
        var finished = false
        try {
            withTimeout(600_000L) {
                // The server sends a heartbeat every 15s, so this long without ANY frame means the
                // connection died without closing (phone asleep, network switch). Give up on it and
                // ask the cache instead of showing "Checking…" forever.
                open().timeout(KIDS_STALL_MS.milliseconds).collect { event ->
                    val progress = parseKidsEvent(event) ?: return@collect
                    send(progress)
                    if (progress !is KidsProgress.Working) {
                        finished = true
                        this@channelFlow.close()
                        return@collect
                    }
                }
            }
            // Stream ended without an answer (server closed it): the pipeline may still finish.
            if (!finished) send(KidsProgress.Failed(recoverable = true))
        } catch (e: kotlinx.coroutines.TimeoutCancellationException) {
            send(KidsProgress.Failed(recoverable = true))
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            val msg = e.message ?: ""
            println("[KIDS] SSE error: ${e::class.simpleName}: $msg")
            send(kidsFailureFor(msg))
        }
    }

    /**
     * Collect a finished analysis, giving the server a moment before declaring failure.
     *
     * `complete` arrives over SSE but the result is collected on a SEPARATE request, so a result
     * that is written-but-not-yet-readable surfaces here as an outright failure. Users were shown
     * "could not fetch result from cache" for analyses that had in fact run — and been billed,
     * since the refund path only fires when the server never emitted `complete`. Spacing out a
     * couple of retries covers that window.
     *
     * Safe to repeat: /api/cached-result verifies an existing charge and never creates one, so
     * extra attempts cannot bill anyone. Every non-race failure just costs the extra wait before
     * the same error it would have shown anyway.
     */
    private suspend fun fetchCachedResultRetrying(cacheRequestBody: String): AnthropicApiClient.CacheFetchOutcome {
        val backoffMs = longArrayOf(500L, 2000L)   // 3 attempts total, ≤2.5s of added waiting
        // Only the FIRST attempt forces a session refresh. It is the one that trails /api/analyze by
        // minutes, so it is the one whose token may have expired in the gap; the retries re-read the
        // session it just refreshed. Refreshing on every attempt would rotate the refresh token three
        // times in 2.5s, which is how you turn a recoverable 401 into a destroyed session.
        var outcome = apiClient.fetchCachedResultDetailed(cacheRequestBody, forceFreshToken = true)
        for (waitMs in backoffMs) {
            if (outcome is AnthropicApiClient.CacheFetchOutcome.Success) return outcome
            val reason = (outcome as AnthropicApiClient.CacheFetchOutcome.Failed).reason
            println("[CACHE-FETCH] $reason — retrying in ${waitMs}ms")
            delay(waitMs)
            outcome = apiClient.fetchCachedResultDetailed(cacheRequestBody)
        }

        // Auth-shaped failure: the analysis itself SUCCEEDED. The server emitted `complete`, so the
        // pipeline finished, the result is cached and the user has already been charged for it —
        // only our collection of it failed. Reporting an error here burns a paid result and is what
        // users experience as "it failed, I had to do it twice, and the review was already there".
        //
        // The backoff above cannot help: it spaces attempts out to outlast a write-visibility race,
        // but the token is missing because the session is momentarily unreadable (Initializing at app
        // start, or RefreshFailure retrying on its own 10s cadence), and no amount of *waiting
        // between requests* changes that — nor does a refresh, which reads the same null accessor.
        // What does change it is the session becoming readable again, so wait for exactly that.
        val reason = (outcome as? AnthropicApiClient.CacheFetchOutcome.Failed)?.reason
        if (reason in AUTH_RECOVERABLE_REASONS) {
            println("[CACHE-FETCH] $reason — waiting up to ${AUTH_RECOVERY_TIMEOUT_MS}ms for a readable session")
            val token = SupabaseAuthService.instance.awaitAccessToken(AUTH_RECOVERY_TIMEOUT_MS)
            if (token != null) {
                println("[CACHE-FETCH] session readable again — collecting the paid result")
                outcome = apiClient.fetchCachedResultDetailed(cacheRequestBody)
            } else {
                println("[CACHE-FETCH] no session within the recovery window — giving up")
            }
        }
        return outcome
    }

    /**
     * Parse SSE event. The "complete" event is lightweight (just score/cost) —
     * the full result is fetched via /api/cached-result to avoid SSE corruption
     * with large JSON payloads.
     */
    private suspend fun parseSseEvent(event: SseEvent, cacheRequestBody: String): AnalysisProgress? {
        return try {
            val data = if (event.data.isNotBlank()) {
                json.parseToJsonElement(event.data).jsonObject
            } else null

            when (event.event) {
                "searching" -> {
                    val step = data?.get("step")?.jsonPrimitive?.intOrNull ?: 0
                    val query = data?.get("query")?.jsonPrimitive?.contentOrNull ?: ""
                    AnalysisProgress.Searching(step, query)
                }
                "search_complete" -> {
                    val step = data?.get("step")?.jsonPrimitive?.intOrNull ?: 0
                    val durationMs = data?.get("durationMs")?.jsonPrimitive?.longOrNull ?: 0L
                    val costUsd = data?.get("costUsd")?.jsonPrimitive?.doubleOrNull ?: 0.0
                    AnalysisProgress.SearchComplete(step, durationMs, costUsd)
                }
                "summarizing" -> AnalysisProgress.Summarizing
                "cache_hit" -> {
                    val layer = data?.get("layer")?.jsonPrimitive?.contentOrNull
                    if (layer == "analysis") {
                        AnalysisProgress.AnalysisCacheHit("")
                    } else {
                        AnalysisProgress.ContentCacheHit("")
                    }
                }
                "analyzing" -> {
                    val title = data?.get("title")?.jsonPrimitive?.contentOrNull ?: ""
                    val totalConcerns = data?.get("totalConcerns")?.jsonPrimitive?.intOrNull ?: 0
                    AnalysisProgress.Analyzing(title, totalConcerns)
                }
                "concern_complete" -> {
                    val index = data?.get("index")?.jsonPrimitive?.intOrNull ?: 0
                    val total = data?.get("total")?.jsonPrimitive?.intOrNull ?: 0
                    val name = data?.get("name")?.jsonPrimitive?.contentOrNull ?: ""
                    AnalysisProgress.ConcernComplete(index, total, name)
                }
                "heartbeat" -> AnalysisProgress.Heartbeat
                "cross_referencing" -> AnalysisProgress.CrossReferencing
                "enriching" -> AnalysisProgress.EnrichingWithGuidance
                "phase1_complete" -> {
                    // V2: Phase 1 verdict ready — partial AnalysisResult embedded inline
                    val resultElement = data?.get("result")
                    if (resultElement != null) {
                        try {
                            val partial = json.decodeFromString<AnalysisResult>(resultElement.toString())
                            AnalysisProgress.Phase1Complete(partial)
                        } catch (e: Exception) {
                            println("[SSE] Failed to parse phase1_complete inline result: ${e.message}")
                            null
                        }
                    } else null
                }
                "phase2_start" -> AnalysisProgress.Phase2Started(
                    total = data?.get("total")?.jsonPrimitive?.intOrNull ?: 0,
                    alreadyDone = data?.get("done")?.jsonPrimitive?.intOrNull ?: 0
                )
                "concern_enriched" -> {
                    // V2: a single Phase 2 unit finished — feeds the "N of M" count under the
                    // details-section loader while the streamed Phase 1 verdict is on screen.
                    val unit = data?.get("unit")?.jsonPrimitive?.contentOrNull
                        ?: data?.get("category")?.jsonPrimitive?.contentOrNull
                        ?: ""
                    AnalysisProgress.ConcernEnriched(unit)
                }
                "verdict_meta_enriched", "developmental_enriched" -> {
                    // V2: bonus enrichments. Surfaced under the server's own unit names so they
                    // match the `pending` list phase2_start sent.
                    AnalysisProgress.ConcernEnriched(event.event.removeSuffix("_enriched"))
                }
                "tldr" -> {
                    val points = data?.get("points")?.let {
                        try { json.decodeFromString<List<TldrPoint>>(it.toString()) } catch (_: Exception) { null }
                    }
                    points?.takeIf { it.isNotEmpty() }?.let { AnalysisProgress.TldrReady(it) }
                }
                "partial_complete" -> {
                    // V2: Phase 2 done but with failures — UI shows retry icons for listed units
                    val failedUnits = data?.get("failedUnits")?.let {
                        try {
                            json.decodeFromString<List<String>>(it.toString())
                        } catch (_: Exception) { emptyList() }
                    } ?: emptyList()
                    AnalysisProgress.PartialComplete(failedUnits)
                }
                "complete" -> {
                    val costUsd = data?.get("costUsd")?.jsonPrimitive?.doubleOrNull ?: 0.0

                    // Check if this is the new lightweight format (no "result" key)
                    // or legacy full format (has "result" key with full AnalysisResult)
                    val inlineResult = data?.get("result")
                    if (inlineResult != null) {
                        // Legacy full format — parse directly
                        val result = json.decodeFromString<AnalysisResult>(inlineResult.toString())
                        AnalysisProgress.AnalysisComplete(result, costUsd)
                    } else {
                        // New lightweight format — fetch full result via HTTP
                        println("[SSE] Received lightweight 'complete' — fetching full result via HTTP")
                        when (val outcome = fetchCachedResultRetrying(cacheRequestBody)) {
                            is AnthropicApiClient.CacheFetchOutcome.Success -> {
                                val result = json.decodeFromString<AnalysisResult>(outcome.json)
                                println("[SSE] Full result fetched successfully (${outcome.json.length} chars)")
                                AnalysisProgress.AnalysisComplete(result, costUsd)
                            }
                            is AnthropicApiClient.CacheFetchOutcome.Failed -> {
                                println("[SSE] Failed to fetch cached result — reason=${outcome.reason}")
                                AnalysisProgress.Error("SSE error: could not fetch result from cache [reason=${outcome.reason}]")
                            }
                        }
                    }
                }
                "error" -> {
                    // Fair-use limit (rolling window, 7 days): a typed rejection, NOT an Error, so none
                    // of the Error recovery paths (auto-retry, resume, paywall, error report) can fire
                    // for it. Older clients never read `code` and just show `message`, which is written
                    // for exactly that. The server sends "usage_cap"; "daily_cap" is what it sent before
                    // October 2026 and is still accepted.
                    val capCode = data?.get("code")?.jsonPrimitive?.contentOrNull
                    if (data != null && (capCode == "usage_cap" || capCode == "daily_cap")) {
                        val retryAfterSeconds = data["retryAfterSeconds"]?.jsonPrimitive?.longOrNull
                        val resetsAtMs = retryAfterSeconds?.let { currentTimeMillis() + it * 1000 }
                            ?: data["resetsAt"]?.jsonPrimitive?.contentOrNull?.let { parseTimestampMillis(it) }
                        AnalysisProgress.DailyLimitReached(
                            DailyLimitNotice(
                                limit = data["limit"]?.jsonPrimitive?.intOrNull,
                                resetsAtMs = resetsAtMs,
                                windowDays = data["windowDays"]?.jsonPrimitive?.intOrNull ?: 7
                            )
                        )
                    } else {
                        val message = data?.get("message")?.jsonPrimitive?.contentOrNull ?: "Unknown error"
                        AnalysisProgress.Error(message)
                    }
                }
                else -> null
            }
        } catch (e: Exception) {
            println("[SSE] Failed to parse event '${event.event}': ${e.message}")
            if (event.event == "complete") {
                // Complete event parse failed — try fetching from cache directly
                println("[SSE] 'complete' event parse failed — attempting cache fetch")
                try {
                    when (val outcome = fetchCachedResultRetrying(cacheRequestBody)) {
                        is AnthropicApiClient.CacheFetchOutcome.Success -> {
                            val result = json.decodeFromString<AnalysisResult>(outcome.json)
                            println("[SSE] Cache fetch succeeded as fallback")
                            AnalysisProgress.AnalysisComplete(result, 0.0)
                        }
                        // The `complete` frame itself was unreadable AND the cache couldn't cover for
                        // it — carry the cache's reason, it's the only diagnostic left.
                        is AnthropicApiClient.CacheFetchOutcome.Failed ->
                            AnalysisProgress.Error("SSE error: result data was corrupted during transfer [reason=${outcome.reason}]")
                    }
                } catch (_: Exception) {
                    AnalysisProgress.Error("SSE error: result data was corrupted during transfer [reason=unparseable]")
                }
            } else null
        }
    }

    /**
     * V2: Re-run a single failed Phase 2 unit. Returns RetryConcernResult.Success with the
     * freshly-enriched payload, RetryConcernResult.Failed if the unit failed again, or
     * RetryConcernResult.Error for transport / precondition issues.
     */
    suspend fun retryConcern(
        title: String,
        mediaType: MediaType,
        criteria: SearchCriteria,
        unit: String,
        year: String? = null,
        info: String? = null
    ): RetryConcernResult {
        val escapedTitle = title.replace("\"", "\\\"")
        val escapedInfo = info?.replace("\"", "\\\"")
        val escapedCustom = criteria.customConcerns.replace("\"", "\\\"")
        val escapedUnit = unit.replace("\"", "\\\"")
        val ageRangesJson = criteria.childAgeRanges.joinToString(",") { "\"$it\"" }
        val body = buildString {
            append("{")
            append("\"title\":\"$escapedTitle\",")
            append("\"mediaType\":\"${mediaType.name}\",")
            if (year != null) append("\"year\":\"$year\",")
            if (escapedInfo != null) append("\"info\":\"$escapedInfo\",")
            append("\"unit\":\"$escapedUnit\",")
            append("\"criteria\":{")
            append("\"childAgeRanges\":[$ageRangesJson],")
            append("\"customConcerns\":\"$escapedCustom\"")
            append("}}")
        }

        val responseText = apiClient.retryConcern(body) ?: return RetryConcernResult.Error("Network error. Please try again.")
        return try {
            val obj = json.parseToJsonElement(responseText).jsonObject
            when (obj["status"]?.jsonPrimitive?.contentOrNull) {
                "success" -> {
                    val payload = obj["payload"]?.toString() ?: "{}"
                    val fullyComplete = obj["fullyComplete"]?.jsonPrimitive?.contentOrNull?.toBooleanStrictOrNull() ?: false
                    RetryConcernResult.Success(unit = unit, payloadJson = payload, fullyComplete = fullyComplete)
                }
                "failed" -> RetryConcernResult.Failed(
                    unit = unit,
                    error = obj["error"]?.jsonPrimitive?.contentOrNull ?: "Unknown error"
                )
                "precondition_missing" -> RetryConcernResult.Error(
                    obj["error"]?.jsonPrimitive?.contentOrNull ?: "Cannot retry: missing prerequisites."
                )
                else -> RetryConcernResult.Error("Unexpected server response.")
            }
        } catch (e: Exception) {
            RetryConcernResult.Error("Failed to parse server response: ${e.message}")
        }
    }

    sealed class RetryConcernResult {
        /** Unit was successfully re-enriched. UI swaps the failed card for the enriched one. payloadJson contains the new enrichment. */
        data class Success(val unit: String, val payloadJson: String, val fullyComplete: Boolean) : RetryConcernResult()
        /** Unit failed again. UI keeps the retry icon. */
        data class Failed(val unit: String, val error: String) : RetryConcernResult()
        /** Transport / precondition / parse error. UI shows a generic error. */
        data class Error(val message: String) : RetryConcernResult()
    }

    private fun friendlyErrorMessage(e: Exception): String {
        val msg = e.message ?: ""
        return when {
            // Billing/auth gates — pass the server marker through UNCHANGED so the ViewModel can
            // raise the paywall (no_credits / 402) or the sign-in flow (auth_required) instead of
            // genericizing it to "Something went wrong". Without this the VM's billing backstop
            // never matched (the marker was stripped here first). The VM intercepts these and shows
            // the paywall / re-auth, so the raw text is never displayed to the user.
            msg.contains("no_credits") || msg.contains("402") -> msg
            msg.contains("auth_required") -> msg
            msg.contains("429") || msg.contains("Rate limit", ignoreCase = true) || msg.contains("Too Many Requests", ignoreCase = true) ->
                "You've reached your limit for the hour. Please try again later."
            msg.contains("401") || msg.contains("Unauthorized", ignoreCase = true) || msg.contains("Authorization", ignoreCase = true) ->
                "Your session has expired. Please sign out and sign back in."
            msg.contains("Unable to resolve host", ignoreCase = true) || msg.contains("No address associated", ignoreCase = true) || msg.contains("network", ignoreCase = true) || msg.contains("internet", ignoreCase = true) || msg.contains("connect", ignoreCase = true) ->
                "No internet connection. Please check your network and try again."
            msg.contains("timeout", ignoreCase = true) ->
                "The request timed out. Please try again."
            msg.contains("500") || msg.contains("502") || msg.contains("503") ->
                "Our servers are temporarily unavailable. Please try again in a moment."
            else -> "Something went wrong. Please try again."
        }
    }
}

// ── Kids Mode: reading the kid-filtered stream ──────────────────────────────
// Top-level (not in the repository) so they can be tested without an API client.

private val kidsEventJson = Json { ignoreUnknownKeys = true }

/** A kids request that failed with [message] (an SSE transport error): who has to act, if anyone. */
internal fun kidsFailureFor(message: String): KidsProgress = when {
    // The server refused this account (KIDZONE not switched on).
    message.contains(KIDS_NOT_ALLOWED_CODE) -> KidsProgress.Blocked(KidsBlock.GROWN_UP_NEEDED, notAllowed = true)
    message.contains("no_credits") || message.contains("402") || message.contains("auth_required") ||
        message.contains("401") || message.contains("Unauthorized", ignoreCase = true) ->
        KidsProgress.Blocked(KidsBlock.GROWN_UP_NEEDED)
    message.contains("429") || message.contains("Rate limit", ignoreCase = true) ||
        message.contains("Too Many Requests", ignoreCase = true) ->
        KidsProgress.Blocked(KidsBlock.DAILY_LIMIT)
    else -> KidsProgress.Failed(recoverable = true)
}

/** One SSE frame of a kid's stream, as progress. Null = ignore it (heartbeat, or an adult-shaped frame). */
internal fun parseKidsEvent(event: SseEvent): KidsProgress? {
    val data = try {
        if (event.data.isNotBlank()) kidsEventJson.parseToJsonElement(event.data).jsonObject else null
    } catch (_: Exception) { null }
    return when (event.event) {
        "kids_result" -> try {
            KidsProgress.Done(kidsEventJson.decodeFromString<KidsResult>(event.data))
        } catch (e: Exception) {
            println("[KIDS] unreadable kids_result: ${e.message}")
            KidsProgress.Failed(recoverable = true)
        }
        "searching", "search_complete" -> {
            val step = data?.get("step")?.jsonPrimitive?.intOrNull ?: 0
            val total = data?.get("total")?.jsonPrimitive?.intOrNull ?: 0
            KidsProgress.Working(if (total > 0) 0.4f * step / total else null)
        }
        "cache_hit", "summarizing" -> KidsProgress.Working(0.45f)
        "analyzing" -> KidsProgress.Working(0.5f)
        "concern_complete" -> {
            val index = data?.get("index")?.jsonPrimitive?.intOrNull ?: 0
            val total = data?.get("total")?.jsonPrimitive?.intOrNull ?: 0
            KidsProgress.Working(if (total > 0) 0.5f + 0.45f * index / total else null)
        }
        // The server says the pipeline finished but had no kid answer to give: ask the cache.
        "kids_done" -> KidsProgress.Failed(recoverable = true)
        // ADULT frames: this server doesn't have Kids Mode (it ignored kids=true). It will never
        // send a kid answer, so stop now rather than wait. The frame itself is never shown.
        "phase1_complete", "complete" -> {
            println("[KIDS] server sent '${event.event}' — it has no Kids Mode; failing closed")
            KidsProgress.Failed(recoverable = false)
        }
        "error" -> {
            val code = data?.get("code")?.jsonPrimitive?.contentOrNull
            val message = data?.get("message")?.jsonPrimitive?.contentOrNull ?: ""
            when {
                // KIDZONE isn't switched on for this account (also on a resume). "Ask a grown-up".
                code == KIDS_NOT_ALLOWED_CODE -> KidsProgress.Blocked(KidsBlock.GROWN_UP_NEEDED, notAllowed = true)
                // "usage_cap" is the weekly fair-use cap; "daily_cap" is what older servers sent.
                code == "usage_cap" || code == "daily_cap" -> KidsProgress.Blocked(KidsBlock.DAILY_LIMIT)
                // Resume found nothing in flight — the result is (or will be) in the cache.
                message == "not_in_flight" -> KidsProgress.Failed(recoverable = true)
                else -> kidsFailureFor(message).let {
                    if (it is KidsProgress.Failed) KidsProgress.Failed(recoverable = false) else it
                }
            }
        }
        // heartbeat and ANY adult-shaped frame (an old server that ignores kids=true) are dropped.
        else -> null
    }
}